import { AbstractValuedItem } from "./AbstractValuedItem";
import { IItem } from "./IItem";
import { AbstractPlainTextItem } from "./AbstractPlainTextItem";
import { IPaintableOnEditing } from "./IPaintableOnEditing";
import { DateItem } from "./DateItem";
import { PaintStyle } from "../../drawing";
import { FuzzyDate, DateDuration, TimeSpan } from "../../system";
import {
  DecimalUtility,
  BinaryFinder,
} from "../../../utilities";
import { INumericRangeValidatable } from "./INumericRangeValidatable";
import { NumericAlertKinds } from "../NumericAlertKinds";
import { NumericRangeValidator } from "../validators";
import { CalculateRoundKinds } from "../CalculateRoundKinds";
import { IPasteRequestable } from "./IPasteRequestable";
import { RelationalActionParameter } from "./RelationalActionParameter";
import { IPasteApplicable } from "./IPasteApplicable";
import { IFontSizeAutoAdjustable } from "./IFontSizeAutoAdjustable";
import { AutoNotePageKinds } from "../AutoNotePageKinds";
import { instanceOfIValueForCalculateExtractable } from "./IValueForCalculateExtractable";
import { AbstractCheckableItem } from "./AbstractCheckableItem";
import { ObjectId } from "../ObjectId";
import { IRelatedExternalScriptExecutable } from "./IRelatedExternalScriptExecutable";
import { IExternalScriptAssociatable } from "./IExternalScriptAssociatable";
import { instanceOfIValueForDateCompareExtractable } from "./IValueForDateCompareExtractable";

export abstract class AbstractExpressionItem extends AbstractPlainTextItem implements IPaintableOnEditing, INumericRangeValidatable, IPasteRequestable, IFontSizeAutoAdjustable, IRelatedExternalScriptExecutable, IExternalScriptAssociatable {
  constructor(typeKey: string, value: any) {
    super(typeKey, value);
  }

  public paintStyleOnEditing: PaintStyle = new PaintStyle();
  public expression: string = "";
  public referenceIds: string[] = [];
  public referenceNames: string[] = [];
  public shouldInputFreeAll: boolean = false;
  /** 数値の上限値・下限値のチェック機能 */
  public numericAlertKind: NumericAlertKinds = NumericAlertKinds.Color;
  public upperLimit: number = null;
  public lowerLimit: number = null;
  public upperLimitAlertColor: string = null;
  public lowerLimitAlertColor: string = null;
  public roundingKind: CalculateRoundKinds = null;
  public roundingPosition: number = null;
  public pasteDestinations: RelationalActionParameter[] = [];
  /** フォントサイズ自動調整 */
  public enabledAutoAdjustFontSize: boolean = false;
  public autoNotePageSize: number = null;            // 計算式・条件式では自動続紙追加はしない
  public autoNotePageKind: AutoNotePageKinds = null; // 計算式・条件式では自動続紙追加はしない

  protected applyMembers(clone: AbstractExpressionItem): void {
    super.applyMembers(clone);
    clone.paintStyleOnEditing = this.cloneValue(this.paintStyleOnEditing);
    clone.expression = this.cloneValue(this.expression);
    clone.referenceIds = this.cloneValue(this.referenceIds);
    clone.referenceNames = this.cloneValue(this.referenceNames);
    clone.shouldInputFreeAll = this.cloneValue(this.shouldInputFreeAll);
    clone.numericAlertKind = this.cloneValue(this.numericAlertKind);
    clone.upperLimit = this.cloneValue(this.upperLimit);
    clone.lowerLimit = this.cloneValue(this.lowerLimit);
    clone.upperLimitAlertColor = this.cloneValue(this.upperLimitAlertColor);
    clone.lowerLimitAlertColor = this.cloneValue(this.lowerLimitAlertColor);
    clone.roundingKind = this.cloneValue(this.roundingKind);
    clone.roundingPosition = this.cloneValue(this.roundingPosition);
    clone.pasteDestinations = this.cloneValue(this.pasteDestinations);
    clone.enabledAutoAdjustFontSize = this.cloneValue(this.enabledAutoAdjustFontSize);
    clone.autoNotePageSize = this.cloneValue(this.autoNotePageSize);
    clone.autoNotePageKind = this.cloneValue(this.autoNotePageKind);
  }

  initialize(
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem[]
  ): IItem {
    return this.getReevaluatedClone(
      s => super.initialize(itemAccessor, itemsAccessor) as AbstractExpressionItem,
      s => s.createReevaluatedCloneOnInitialize(itemAccessor, itemsAccessor),
    );
  }

  private createReevaluatedCloneOnInitialize(itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): AbstractExpressionItem {
    return this.shouldCalculate
      ? this.createReevaluatedClone(itemAccessor, itemsAccessor)
      : null;
  }

  handleItemUpdated(
    originalItem: IItem,
    updatedItem: IItem,
    isInSameSection: boolean,
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]
  ): IItem {
    return this.getReevaluatedClone(
      s => super.handleItemUpdated(originalItem, updatedItem, isInSameSection, itemAccessor, itemsAccessor) as AbstractExpressionItem,
      s => s.createReevaluatedCloneOnItemUpdated(updatedItem, itemAccessor, itemsAccessor),
    );
  }

  private createReevaluatedCloneOnItemUpdated(updatedItem: IItem, itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): AbstractExpressionItem {
    return (!this.shouldInputFree && !this.shouldInputFreeAll && this.isReferedItem(updatedItem))
      ? this.createReevaluatedClone(itemAccessor, itemsAccessor)
      : null;
  }

  private isReferedItem(updatedItem: IItem): boolean {
    return !!AbstractExpressionItem._idFinder.find(updatedItem.id, this.referenceIds)
        || !!AbstractExpressionItem._nameFinder.find((updatedItem as AbstractValuedItem).name, this.referenceNames);
  }

  private static readonly _idFinder = new BinaryFinder((s: string) => s);
  private static readonly _nameFinder = new BinaryFinder((s: string) => s);

  handleOwnUpdated(
    originalItem: IItem,
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]
  ): IItem {
    return this.getReevaluatedClone(
      s => super.handleOwnUpdated(originalItem, itemAccessor, itemsAccessor) as AbstractExpressionItem,
      s => s.createReevaluatedCloneOnOwnUpdated(originalItem, itemAccessor, itemsAccessor),
    );
  }

  private createReevaluatedCloneOnOwnUpdated(originalItem: IItem, itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): AbstractExpressionItem {
    return (!this.shouldInputFree && !this.shouldInputFreeAll && this.isFreeInputChanged(originalItem))
      ? this.createReevaluatedClone(itemAccessor, itemsAccessor)
      : null;
  }

  private isFreeInputChanged(originalItem: IItem): boolean {
    return (originalItem instanceof AbstractExpressionItem)
        && (originalItem.shouldInputFree !== this.shouldInputFree || originalItem.shouldInputFreeAll !== this.shouldInputFreeAll);
  }

  private getReevaluatedClone(...updates: ((s: AbstractExpressionItem) => AbstractExpressionItem)[]): AbstractExpressionItem {
    const updated = this.getUpdatedClone<AbstractExpressionItem>(...updates);
    if (updated) {
      updated.adjustFontSize();
    }
    return updated;
  }

  createReevaluatedClone(itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): AbstractExpressionItem {
    const newValue = this.toEvaluatedValue(itemAccessor, itemsAccessor);
    const formattedNewValue = this.toFormattedValue(newValue);
    return (this.value !== formattedNewValue)
      ? this.createUpdatedClone<AbstractExpressionItem>(s => s.value = formattedNewValue)
      : null;
  }

  protected abstract toFormattedValue(value: any): any;

  toEvaluatedValue(itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): any {
    try {
      const asNumber = (key: number | string) => this.toNumber(this.findItem(itemAccessor, itemsAccessor, key));
      const asString = (key: number | string) => this.toObjectValueString(this.findItem(itemAccessor, itemsAccessor, key));
      const asNumberOr = (key: number | string, defaultValue: number) => this.toNumberOr(this.findItem(itemAccessor, itemsAccessor, key), defaultValue);
      const asStringOr = (key: number | string, defaultValue: string) => this.toStringOr(this.findItem(itemAccessor, itemsAccessor, key), defaultValue);
      const asDuration = (from: number | string, to: number | string, shouldContainCurrent: boolean) => this.toDuration(this.findItem(itemAccessor, itemsAccessor, from), this.findItem(itemAccessor, itemsAccessor, to), shouldContainCurrent);
      const asSpan = (from: number | string, to: number | string) => this.toSpan(this.findItem(itemAccessor, itemsAccessor, from), this.findItem(itemAccessor, itemsAccessor, to));
      const ln = (source: number) => Math.log(source);
      const power = (source: number, exponent: number) => Math.pow(source, exponent);
      const squareRoot = (source: number) => Math.sqrt(source);
      const asDateNumber = (key: number | string) => this.toDateNumber(this.findItem(itemAccessor, itemsAccessor, key));
      const isTrue = (expressionAsBase64: string) => this.isTrue(
        expressionAsBase64,
        asNumber,
        asString,
        asNumberOr,
        asStringOr,
        asDuration,
        asSpan,
        ln,
        power,
        squareRoot,
        asDateNumber);
      return this.runEvaluation(
        asNumber,
        asString,
        asNumberOr,
        asStringOr,
        asDuration,
        asSpan,
        ln,
        power,
        squareRoot,
        isTrue,
        asDateNumber
      );
    } catch (e) {
      console.warn("計算できませんでした.");
      console.warn(this.expression);
      console.warn(e);
      return null;
    }
  }

  private findItem(itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[], key: number | string): IItem {
    // TODO: 現状では001のような数値は式の評価のタイミングでエラーになる
    // TODO: バックエンド側でobj-idをシングルクォーテーションで囲うと名称と区別がつかなくなるので対応が必要になる見込み
    const wrappedKey = (typeof key === "number") ? new ObjectId(key.toString()) : key;
    const item = itemAccessor(wrappedKey);
    if (!item) {
      // オブジェクトが見つからない場合、使用する式の側でエラーとするかデフォルト値を使用するかを判定する
      return null;
    }
    if (typeof key === "string" && item instanceof AbstractCheckableItem) {
      // CheckableItemの名称指定の場合は評価すべきItemを探す必要あり.
      const checkableItems = itemsAccessor((i: any) => {
        return (i && i.name === key);
      });
      for (const checkableItem of checkableItems) {
        if (checkableItem instanceof AbstractCheckableItem && checkableItem.isChecked) {
          return checkableItem;
        }
      }
      // 選択されていない値を返却する
      // (V1と動作を合わせこむための対応)
      for (const checkableItem of checkableItems) {
        if (checkableItem instanceof AbstractCheckableItem && !checkableItem.isChecked) {
          return checkableItem;
        }
      }
      // オブジェクトが見つからない場合、使用する式の側でエラーとするかデフォルト値を使用するかを判定する
      return null;
    }
    return item;
  }

  private isTrue(
    expressionAsBase64: string,
    asNumber: (key: number | string) => number,
    asString: (key: number | string) => string,
    asNumberOr: (key: number | string, defaultValue: number) => number,
    asStringOr: (key: number | string, defaultValue: string) => string,
    asDuration: (from: number | string, to: number | string, isContainToday: boolean) => any,
    asSpan: (from: number | string, to: number | string) => any,
    ln: (source: number) => number,
    power: (source: number, exponent: number) => number,
    squareRoot: (source: number) => number,
    asDateNumber: (key: number | string) => number
  ): any {
    try {
      const isTrue = (expAsBase64: string) => this.isTrue(
        expAsBase64,
        asNumber,
        asString,
        asNumberOr,
        asStringOr,
        asDuration,
        asSpan,
        ln,
        power,
        squareRoot,
        asDateNumber);
      if (isTrue) {}  // isTrueはevalでの評価時に必要なら参照されるがTSLINTが参照されてないとエラー出すので意味ないが分岐作って回避.(暫定対応.)
      const expression = decodeURIComponent(escape(atob(expressionAsBase64)));
      const result = eval(expression);
      if (typeof(result) !== "boolean") {
        throw new Error("条件判定結果がboolean以外でした.");  // 独自例外を定義する？
      }
      return result;
    } catch {
      return false; // 例外発生した場合でも他の条件式は実行されるべきなのでfalseを返す.
    }
  }

  private runEvaluation(
    // idの場合は数値、nameの場合は文字列(シングルクォートで囲まれている)で渡される.
    asNumber: (key: number | string) => number,
    asString: (key: number | string) => string,
    asNumberOr: (key: number | string, defaultValue: number) => number,
    asStringOr: (key: number | string, defaultValue: string) => string,
    asDuration: (from: number | string, to: number | string, isContainToday: boolean) => any,
    asSpan: (from: number | string, to: number | string) => any,
    ln: (source: number) => number,
    power: (source: number, exponent: number) => number,
    squareRoot: (source: number) => number,
    isTrue: (expressionAsBase64: string) => boolean,
    asDateNumber: (key: number | string) => number
  ): any {
    const result = eval(this.expression);
    if (typeof(result) === "number" && isNaN(result)) {
      throw new Error("計算結果がNaNでした.");  // 独自例外を定義する？
    }
    if (typeof(result) === "number" && !isFinite(result)) {
      throw new Error("計算結果が有理数ではありませんでした.");  // 独自例外を定義する？
    }
    return (typeof(result) !== "number")
      ? result
      : DecimalUtility.format(this.roundCalclatedNumber(result), this.roundingPosition);
  }

  private toNumber(item: IItem): number {
    if (!item) {
      throw new Error("該当するitemが見つかりませんでした."); // 独自例外を定義する？
    }
    const converted = this.extractValue(item);
    if (converted === null || converted === undefined || converted === "") {
      throw new Error("数値を取得できませんでした.値が空です.");  // 独自例外を定義する？
    }
    return this.toSafeNumber(converted);
  }

  private toSafeNumber(value: any): number {
    if (isNaN(value)) {
      throw new Error("数値を取得できませんでした.");  // 独自例外を定義する？
    }
    // 半角スペース・全角スペースがエラーにならずに0となってしまうので、
    // 文字列の場合は少なくとも一つは数値を含んでいるかどうか判定して対策している
    if (typeof value === "string" && !(/[0-9]/.test(value))) {
      throw new Error("数値を取得できませんでした.");  // 独自例外を定義する？
    }
    const num = Number(Number(value).toFixed(16));
    const max = Number.MAX_SAFE_INTEGER || Math.pow(2, 53) - 1;     // IEは未定義のため
    const min = Number.MIN_SAFE_INTEGER || -(Math.pow(2, 53) - 1);  // IEは未定義のため
    if (num < min || num > max) {
      throw new Error("数値を取得できませんでした.整数値範囲外");  // 独自例外を定義する？
    }
    return num;
  }

  private extractValue(item: IItem): any {
    if (instanceOfIValueForCalculateExtractable(item)) {
      return item.extractValueForCalculate();
    }
    return (item as AbstractValuedItem).value;
  }

  private toDateNumber(item: IItem): number {
    if (!item) {
      throw new Error("該当するitemが見つかりませんでした."); // 独自例外を定義する？
    }
    const converted = this.extractDateNumberValue(item);
    if (converted == null || !isFinite(converted)) {
      throw new Error("日付の値を取得できませんでした.");  // 独自例外を定義する？
    }
    return converted;
  }

  private extractDateNumberValue(item: IItem): number {
    if (instanceOfIValueForDateCompareExtractable(item)) {
      return item.extractValueForDateCompare();
    }
    return null;
  }

  /**
   * toStringだとoverrideしてしまうので名称変更している
   */
  private toObjectValueString(item: IItem): string {
    if (!item) {
      throw new Error("該当するitemが見つかりませんでした."); // 独自例外を定義する？
    }
    const value = this.extractValue(item);
    return (value !== null && value !== undefined) ? value.toString() : "";
  }

  private toNumberOr(item: IItem, defaultValue: number): number {
    if (!item) {
      return defaultValue;
    }
    const value = this.extractValue(item);
    return (value !== null && value !== undefined && value !== "")
      ? this.toNumber(item)
      : defaultValue;
  }

  private toStringOr(item: IItem, defaultValue: string): string {
    if (!item) {
      return defaultValue;
    }
    const value = this.extractValue(item);
    return (value !== null && value !== undefined && value !== "")
      ? this.toObjectValueString(item)
      : defaultValue;
  }

  private toDuration(from: IItem, to: IItem, shouldContainCurrent: boolean): DateDuration {
    if (!from || !to) {
      throw new Error("該当するitemが見つかりませんでした."); // 独自例外を定義する？
    }
    // 期間日数のfromとtoは逆指定されてる場合は入れ替える。
    // (v1とは挙動違うがDocu2Web初版実装仕様を踏襲する。おそらく最終的にはそんなおかしい情報は入れなくて途中の表示はあまり気にされてない。)
    const fromDate = this.toDate(from, false);
    const toDate = this.toDate(to, false);
    return (fromDate <= toDate)
      ? DateDuration.fromTo(fromDate, toDate, shouldContainCurrent)
      : DateDuration.fromTo(toDate, fromDate, shouldContainCurrent);
  }

  private toDate(source: IItem, isTime: boolean): Date {
    if (!source) {
      throw new Error("sourceが無効です。");
    }
    if (!(source instanceof DateItem)) {
      throw new Error("DateItemではありません。(" + source.id + ")");
    }
    const value = source.value;
    if (!(value instanceof FuzzyDate)) {
      throw new Error("valueがFuzzyDateではありません。(" + source.id + ")");
    }
    if (value.isTime !== isTime) {
      throw new Error("isTimeが一致しません。(" + source.id + ")");
    }
    if (!(value.value instanceof Date)) {
      throw new Error("valueは曖昧です.(" + source.id + ")");
    }
    return value.value;
  }

  private toSpan(from: IItem, to: IItem): any {
    if (!from || !to) {
      throw new Error("該当するitemが見つかりませんでした."); // 独自例外を定義する？
    }
    return TimeSpan.fromTo(
      this.toDate(from, true),
      this.toDate(to, true),
      true
    );
  }

  isNumericValueInRange(): boolean {
    const validateValue = this.getRangeValidateValue();
    return NumericRangeValidator.isInRange(validateValue, this.upperLimit, this.lowerLimit);
  }

  getNumericRangeAlertColor(): string {
    if (this.numericAlertKind !== NumericAlertKinds.Color) { return null; }
    const validateValue = this.getRangeValidateValue();
    if (!NumericRangeValidator.isInRange(validateValue, this.upperLimit, null)) {
      return this.upperLimitAlertColor;
    }
    if (!NumericRangeValidator.isInRange(validateValue, null, this.lowerLimit)) {
      return this.lowerLimitAlertColor;
    }
    return null;
  }

  protected abstract getRangeValidateValue(): string;

  private roundCalclatedNumber(result: number): number {
    const errorCorrected = DecimalUtility.roundUnacceptableError(result);
    if (this.roundingPosition == null || this.roundingPosition === undefined) { return errorCorrected; }

    switch (this.roundingKind) {
    case CalculateRoundKinds.RoundOff:
      return DecimalUtility.roundOff(errorCorrected, this.roundingPosition - 1);
    case CalculateRoundKinds.RoundUp:
      return DecimalUtility.roundUp(errorCorrected, this.roundingPosition - 1);
    case CalculateRoundKinds.RoundDown:
      return DecimalUtility.roundDown(errorCorrected, this.roundingPosition - 1);
    default:
      return errorCorrected;
    }
  }

  pasteTo(item: IPasteApplicable): void {
    const text = this.valueToText(this.value);
    item.applyPastedValue(text, undefined);
  }
  
  public setText(text: string): void {
    this.value = text;
  }

  adjustFontSize(): void {
    if (!this.enabledAutoAdjustFontSize) { return; }
    const adjustedFontSize = this.getAdjustedFontSize(this.textStyle.isMultiLine);
    this.textStyle.adjustedFontSize = adjustedFontSize;
  }
  
  get shouldCalculate(): boolean {
    return !this.shouldInputFree;
  }

  get isEmpty(): boolean {
    return (this.value === null || this.value === undefined || this.value === "");
  }

  isValueUpdated(oldItem: any): boolean {
    // TODO: implement
    return true;
  }

  getValueForExternalScript(): string {
    return this.text();
  }

  setValueFromExternalScript(value: string): void {
    // 計算式・条件式への外部処理からの入力には対応しない
  }
}
